import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import { tokens } from "../../../../../theme";
import { EmailTemplateStepProps } from "../../types/types";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
};

interface UploadFieldProps {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  onUploadFile: (file: File) => Promise<string>;
}

const UploadField: React.FC<UploadFieldProps> = ({
  label,
  hint,
  value,
  onChange,
  onUploadFile,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await onUploadFile(file);
      onChange(url);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 500,
          color: colors.primary[700],
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="https//:..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={fieldSx}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileSelected}
        />
        <Button
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          startIcon={
            uploading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <UploadIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            bgcolor: colors.primary[400],
            "&:hover": { bgcolor: colors.primary[500] },
            borderRadius: "10px",
            textTransform: "none",
            px: 2.5,
            whiteSpace: "nowrap",
          }}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </Box>
      <Typography
        variant="caption"
        sx={{ color: colors.primary[300], display: "block", mt: 0.75 }}
      >
        {hint}
      </Typography>
    </Box>
  );
};

const MediaStep: React.FC<EmailTemplateStepProps> = ({
  templateData,
  setTemplateData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const setField = (field: keyof typeof templateData) => (value: string) => {
    setTemplateData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTextChange =
    (field: keyof typeof templateData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTemplateData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  // TODO: wire to real media upload endpoint once backend exists; returns a hosted URL
  const uploadFile = async (file: File): Promise<string> => {
    await new Promise((resolve) => setTimeout(resolve, 800));
    return URL.createObjectURL(file);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: colors.primary[700] }}
        >
          Media
        </Typography>
        <Typography variant="body2" sx={{ color: colors.primary[300] }}>
          Logo, banner & footer
        </Typography>
      </Box>

      <UploadField
        label="Logo URL"
        hint="Recommended 240×80 PNG. Leave blank to use the default Muzukuru wordmark."
        value={templateData.logo_url}
        onChange={setField("logo_url")}
        onUploadFile={uploadFile}
      />

      <UploadField
        label="Banner image URL"
        hint="Recommended 1200×400. Appears at the top of the email."
        value={templateData.banner_url}
        onChange={setField("banner_url")}
        onUploadFile={uploadFile}
      />

      <Box>
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 500,
            color: colors.primary[700],
            mb: 0.75,
          }}
        >
          Footer Message
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={templateData.footer_message}
          onChange={handleTextChange("footer_message")}
          sx={fieldSx}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: colors.primary[700],
              mb: 0.75,
            }}
          >
            Contact Email
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={templateData.contact_email}
            onChange={handleTextChange("contact_email")}
            sx={fieldSx}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: colors.primary[700],
              mb: 0.75,
            }}
          >
            Contact Phone
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={templateData.contact_phone}
            onChange={handleTextChange("contact_phone")}
            sx={fieldSx}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MediaStep;
